import { Helmet } from "react-helmet-async";
import React, { useContext, useEffect, useReducer, useState } from "react";
import useBodyLock from "../utils/useBodyLock";
import { Data } from "../../Data";
import Axios from "axios";
import { toast } from "sonner";
import { getDate, getError } from "../../utils/utils";
import { useSearchParams, Link } from "react-router-dom";
import { useNavCountsContext } from "../utils/NavCountsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faDownload } from "@fortawesome/free-solid-svg-icons";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Spinner } from "../../components/ui/spinner";
import { Skeleton } from "../../components/ui/skeleton";
import { Slider } from "../../components/ui/slider";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogBody, DialogFooter, DialogCloseButton,
} from "../../components/ui/dialog";

const PAYMENT_DAYS = [1, 5, 10, 15, 20, 24, 28];

function PaymentDaySlider({ value, onChange }) {
    const idx = PAYMENT_DAYS.indexOf(value);
    const currentIdx = idx === -1 ? 3 : idx;

    const handleChange = ([newIdx]) => {
        onChange(PAYMENT_DAYS[newIdx]);
    };

    return (
        <div className="mt-1 mb-1">
            <Slider
                min={0}
                max={PAYMENT_DAYS.length - 1}
                step={1}
                value={[currentIdx]}
                onValueChange={handleChange}
            />
            <div className="flex justify-between mt-2">
                {PAYMENT_DAYS.map((day, i) => (
                    <button
                        key={day}
                        type="button"
                        onClick={() => onChange(day)}
                        className={`flex flex-col items-center gap-0.5 w-8 -mx-1 focus:outline-none group`}
                    >
                        <span className={`text-xs font-semibold leading-none transition-colors ${i === currentIdx ? "text-[#082359]" : "text-muted-foreground group-hover:text-foreground"}`}>
                            {day}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

const reducer = (state, action) => {
    switch (action.type) {
        case "FETCH_REQUEST": return { ...state, loading: true };
        case "FETCH_SUCCESS": return { ...state, infoData: action.payload, loading: false };
        case "FETCH_FAIL": return { ...state, loading: false };
        case "SIGN_REQUEST": return { ...state, singLoading: true };
        case "SIGN_SUCCESS": return { ...state, signData: action.payload, singLoading: false };
        case "SIGN_FAIL": return { ...state, singLoading: false };
        case "SIGN_REQUEST_BANKID": return { ...state, bankidLoading: true };
        case "SIGN_SUCCESS_BANKID": return { ...state, bankidLoading: false };
        case "SIGN_FAIL_BANKID": return { ...state, bankidLoading: false };
        default: return state;
    }
};

const BankIdLogo = () => (
    <svg width="190" height="28" viewBox="0 0 190 38" fill="none" xmlns="http://www.w3.org/2000/svg"
        className="w-[80px] pr-[10px] mr-[10px] shrink-1 max-w-full" style={{ borderRight: "1px solid #fff" }}>
        <path d="M162.165 7.511h8.601c6.777 0 10.165 5.634 10.165 11.216 0 5.634-3.388 11.215-10.165 11.215h-8.601v-2.608h3.649V10.12h-3.649V7.51zm-4.952 29.473h13.814c12.354 0 18.506-9.128 18.506-18.257 0-9.13-6.152-18.258-18.506-18.258h-13.814v36.515zM143.139 7.511h7.819V.47h-7.819v7.042zm0 2.609h7.819v26.864h-7.819V10.12zm-32.528 26.864v-8.972h2.606l7.82 8.972h7.767v-2.608l-9.852-11.059 9.591-10.59V10.12h-7.454l-7.872 8.972v3.6h-2.606V.468h-7.819v36.515h7.819zm-21.633 0h7.819V20.71c0-6.73-4.9-11.111-10.374-11.111-2.294 0-4.691.782-6.829 2.452v3.025h-2.606V10.12H70.21v26.864h7.82V22.117c0-3.755 2.867-5.738 5.681-5.738 2.659 0 5.266 1.774 5.266 5.478v15.127zM49.465 16.275c3.492 0 6.411 2.817 6.411 7.25 0 4.435-2.919 7.252-6.411 7.252-3.493 0-6.62-2.817-6.62-7.251s3.127-7.251 6.62-7.251zm-14.7 7.25c0 8.66 6.203 13.981 12.354 13.981 2.45 0 4.9-.835 6.933-2.712v-2.765h2.606v4.955h7.298V10.12h-7.298v4.955h-2.606v-2.764c-2.033-1.879-4.483-2.713-6.933-2.713-6.151 0-12.355 5.32-12.355 13.928zM8.23 6.99h9.488c2.919 0 4.43 2.086 4.43 4.173 0 2.086-1.511 4.173-4.43 4.173h-6.881v6.781h7.558c2.92 0 4.431 2.087 4.431 4.174 0 2.086-1.512 4.173-4.43 4.173H8.23V6.99zm19.6 12.78h-4.274v-2.608h3.753c2.138-1.774 3.232-4.33 3.232-6.938 0-4.851-3.805-9.755-11.729-9.755H.151v36.515h19.392c7.976 0 11.834-5.06 11.834-10.015 0-2.765-1.2-5.425-3.545-7.199z" fill="#fff" />
    </svg>
);

const SigningHelpBanner = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-muted-foreground" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
                </svg>
            </div>
            <div>
                <p className="text-sm font-medium text-foreground">Potřebujete pomoc s podpisem?</p>
                <p className="text-xs text-muted-foreground">Pošlete nám žádost a ozveme se vám.</p>
            </div>
        </div>
        <Button asChild variant="outline" size="sm">
            <Link to="/requests">Vytvořit žádost</Link>
        </Button>
    </div>
);

const ContactCard = () => (
    <div className="bg-white rounded-xl border border-border shadow-sm p-6">
        <div className="flex flex-wrap items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-blue-600" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
            </div>
            <div>
                <p className="text-sm font-semibold text-foreground mb-0.5">Potřebujete pomoc s podpisem?</p>
                <p className="text-sm text-muted-foreground mb-3">Kontaktujte nás — rádi pomůžeme.</p>
                <div className="flex flex-col gap-2">
                    <a href="tel:+420226281001" className="flex items-center gap-2 text-sm text-[#082359] hover:underline underline-offset-2 font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                        </svg>
                        +420 226 281 001
                    </a>
                    <a href="mailto:pohledavky@devriesgroup.cz" className="flex items-center gap-2 text-sm text-[#082359] hover:underline underline-offset-2 font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                        </svg>
                        pohledavky@devriesgroup.cz
                    </a>
                </div>
            </div>
        </div>
    </div>
);

function DocumentsSkeleton() {
    return (
        <div className="space-y-4">
            {[1, 2].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-border shadow-sm p-5 mb-5">
                    <div className="flex gap-4 mb-4">
                        <div className="space-y-2 flex-1">
                            <Skeleton className="h-3 w-24" />
                            <Skeleton className="h-5 w-36" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-5 w-28" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Skeleton className="h-24 rounded-lg" />
                        <Skeleton className="h-24 rounded-lg" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function Documents() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [{ loading, singLoading, bankidLoading, signData, infoData }, dispatch] = useReducer(reducer, {
        infoData: [], signData: [], singLoading: false, loading: true, bankidLoading: false,
    });
    const [showAuth, setShowAuth] = useState(false);
    const [paymentDays, setPaymentDays] = useState({});
    const { state } = useContext(Data);
    const { userInfo } = state;
    const { triggerNavRefetch } = useNavCountsContext();

    useBodyLock(bankidLoading || singLoading);

    const getPaymentDay = (key) => paymentDays[key] ?? 15;
    const setPaymentDay = (key, day) => setPaymentDays((prev) => ({ ...prev, [key]: day }));

    const signDocument = async (e, refNo, documentId, currentValue, paymentDayValue) => {
        e.preventDefault();
        dispatch({ type: "SIGN_REQUEST" });
        try {
            const { data } = await Axios.post("/api/documents/sign", {
                id: documentId, refNo, nextPamentDate: paymentDayValue ?? null, currentValue: parseInt(currentValue),
            }, { headers: { authorization: `Bearer ${userInfo.token}` } });
            dispatch({ type: "SIGN_SUCCESS", payload: data });
            setShowAuth(true);
        } catch (err) {
            dispatch({ type: "SIGN_FAIL" });
            toast.error(getError(err));
        }
    };

    useEffect(() => {
        if (searchParams.has("code")) {
            const queryCode = searchParams.get("code");
            dispatch({ type: "SIGN_REQUEST_BANKID" });
            const bankidHandler = async () => {
                try {
                    await Axios.post("/api/documents/callback", { code: queryCode }, {
                        headers: { authorization: `Bearer ${userInfo.token}` },
                    });
                    dispatch({ type: "SIGN_SUCCESS_BANKID" });
                    const { data } = await Axios.post("/api/documents/info", { request: "info" }, {
                        headers: { authorization: `Bearer ${userInfo.token}` },
                    });
                    dispatch({ type: "FETCH_SUCCESS", payload: data });
                    triggerNavRefetch();
                    toast.success("Dokument úspěšně podepsán");
                } catch (err) {
                    dispatch({ type: "SIGN_FAIL_BANKID" });
                    toast.error(getError(err));
                }
            };
            bankidHandler();
            searchParams.delete("code");
            if (searchParams.has("traceId")) searchParams.delete("traceId");
            if (searchParams.has("state")) searchParams.delete("state");
            setSearchParams(searchParams);
        }
    }, [userInfo, searchParams, setSearchParams, triggerNavRefetch]);

    useEffect(() => {
        const fetchData = async () => {
            dispatch({ type: "FETCH_REQUEST" });
            try {
                const { data } = await Axios.post("/api/documents/info", { request: "info" }, {
                    headers: { authorization: `Bearer ${userInfo.token}` },
                });
                dispatch({ type: "FETCH_SUCCESS", payload: data });
            } catch (err) {
                dispatch({ type: "FETCH_FAIL" });
                toast.error(getError(err));
            }
        };
        fetchData();
    }, [userInfo]);

    const cases = Array.isArray(infoData.cases) ? infoData.cases : [];
    const signedDocuments = Array.isArray(infoData.signedDocuments) ? infoData.signedDocuments : [];
    const casesWithUnsigned = cases.filter((c) => Array.isArray(c.signId) && c.signId.some((s) => !s.isSigned));
    const hasAnyContent = casesWithUnsigned.length > 0 || signedDocuments.length > 0;

    return (
        <>
            <Helmet><title>Dokumenty | De Vries Group, a.s.</title></Helmet>

            {(bankidLoading || singLoading) && (
                <div className="loading-box-global">
                    <Spinner size="xl" />
                    <p className="text-lg font-medium">Načítání, prosím vyčkejte…</p>
                </div>
            )}

            <Dialog open={showAuth} onOpenChange={(open) => { if (!open) setShowAuth(false); }}>
                <DialogContent size="lg" className="relative max-h-[90vh] flex flex-col">
                    <DialogCloseButton />
                    <DialogHeader>
                        <DialogTitle>{signData.documentName}</DialogTitle>
                    </DialogHeader>
                    <DialogBody className="overflow-y-auto flex-1">
                        <div className="flex flex-col items-center text-center gap-1">
                            <h3 className="text-base font-medium">Náhled dokumentu</h3>
                            <embed className="block lg:hidden mb-4" src={signData.documentURI} width="100%" height="400px" />
                            <embed className="hidden lg:block mb-4" src={signData.documentURI} width="500px" height="650px" />
                            <div className="flex flex-wrap gap-2 items-center justify-center">
                                <Button asChild>
                                    <a href={signData.documentURI} target="_blank" rel="noreferrer">
                                        Zobrazit <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                    </a>
                                </Button>
                                <Button asChild>
                                    <a href={signData.documentURI} target="_blank" rel="noreferrer" download>
                                        Stáhnout <FontAwesomeIcon icon={faDownload} />
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <a href={signData.url} className="button-bankid">
                            <BankIdLogo />Zobrazit a podepsat
                        </a>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <main className="py-8 px-6 max-w-5xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-foreground">Dokumenty</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Dokumenty k podpisu a podepsané dokumenty</p>
                </div>

                {loading ? (
                    <DocumentsSkeleton />
                ) : !hasAnyContent ? (
                    /* No documents at all */
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center">
                            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-muted-foreground" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                                </svg>
                            </div>
                            <p className="text-sm font-medium text-foreground mb-1">Žádné dokumenty k zobrazení</p>
                            <p className="text-xs text-muted-foreground">Momentálně nemáte žádné dokumenty k podpisu.</p>
                        </div>
                        <ContactCard />
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Documents requiring signature — only show cases that have at least one unsigned doc */}
                        {casesWithUnsigned.length > 0 && (
                            <section aria-labelledby="to-sign-heading">
                                <h2 className="text-base font-semibold text-foreground mb-3" id="to-sign-heading">K podpisu</h2>
                                <div className="space-y-4">
                                    {casesWithUnsigned.map((item, index) => (
                                        <div key={index} className="bg-white rounded-xl border border-border shadow-sm mb-5 p-5">
                                            <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 pb-4 border-b border-border">
                                                <div>
                                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Referenční číslo</p>
                                                    <p className="text-base font-semibold">{item.refNo}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Věřitel</p>
                                                    <p className="text-base font-semibold">{item.supplier || "Není vyplněn"}</p>
                                                </div>
                                            </div>

                                            {item.signId.length === 0 ? (
                                                <p className="text-sm text-muted-foreground">Žádné dokumenty k podpisu.</p>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                    {item.signId.map((sign, signIndex) => (
                                                        <div
                                                            key={signIndex}
                                                            className={`relative rounded-lg border p-4 ${!sign.isSigned ? "bg-blue-50/50 border-blue-200" : "bg-green-50/50 border-green-200"}`}
                                                        >
                                                            {!sign.isSigned && (
                                                                <span className="absolute -top-2.5 left-4 text-[10px] bg-red-500 text-white font-semibold px-2 py-0.5 rounded-full">
                                                                    Čeká na podpis
                                                                </span>
                                                            )}
                                                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 mt-1">Název</p>
                                                            <h4 className="text-sm font-semibold mb-3">{sign.name}</h4>

                                                            {sign.isSigned ? (
                                                                <Badge variant="success">Úspěšně podepsáno</Badge>
                                                            ) : (
                                                                <>
                                                                    {sign.settingsEnabled && (
                                                                        <div className="bg-white rounded-lg p-4 mb-3 border border-border">
                                                                            {sign.valueFixed ? (
                                                                                sign.valueFixedType === "asc_price_months" ? (
                                                                                    <>
                                                                                        {parseFloat(item.amount) <= parseFloat(sign.nearestKey) ? (
                                                                                            sign.nearestValue > 1 ? (
                                                                                                <form onSubmit={(e) => signDocument(e, item.refNo, sign.id, document.getElementById("value" + index + index + index + index)?.textContent, sign.nearestValue > 1 ? getPaymentDay(`${index}-${signIndex}-asc`) : null)}>
                                                                                                    <p className="text-sm mb-1"><strong>Počet splátek:</strong> {sign.nearestValue}</p>
                                                                                                    <p className="text-sm mb-1"><strong>První splátka:</strong> Za 15 dní od podpisu</p>
                                                                                                    <p className="text-sm mb-1"><strong>Částka za splátku:</strong> {parseFloat(item.amount / sign.nearestValue).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč</p>
                                                                                                    <p className="text-sm mb-3"><strong>Celková částka:</strong> {parseFloat(item.amount).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč</p>
                                                                                                    {sign.nearestValue > 1 && (
                                                                                                        <div className="mb-3">
                                                                                                            <p className="text-sm font-semibold mb-2">Datum dalších splátek:</p>
                                                                                                            <p className="text-xs text-muted-foreground mb-3">
                                                                                                                Vždy <strong>{getPaymentDay(`${index}-${signIndex}-asc`)}</strong>. den v měsíci
                                                                                                            </p>
                                                                                                            <PaymentDaySlider
                                                                                                                value={getPaymentDay(`${index}-${signIndex}-asc`)}
                                                                                                                onChange={(day) => setPaymentDay(`${index}-${signIndex}-asc`, day)}
                                                                                                            />
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <button type="submit" className="button-bankid mt-2 flex"><BankIdLogo />Zobrazit a podepsat</button>
                                                                                                </form>
                                                                                            ) : (
                                                                                                <p className="text-sm">Zaplatíte pouze 1 splátku za 15 dní od podpisu.</p>
                                                                                            )
                                                                                        ) : (
                                                                                            <p className="text-sm text-muted-foreground">Splátky pro Vás nejsou dostupné, kontaktujte administrátora.</p>
                                                                                        )}
                                                                                    </>
                                                                                ) : (
                                                                                    <ContactCard />
                                                                                )
                                                                            ) : (
                                                                                <form onSubmit={(e) => signDocument(e, item.refNo, sign.id, document.getElementById("value" + index + index + index + index)?.textContent)}>
                                                                                    <label htmlFor="valueRange" className="block text-sm font-medium mb-1">
                                                                                        {sign.settingsName} <em className="text-muted-foreground text-xs">(posunutím vyberete)</em>
                                                                                    </label>
                                                                                    <div className="flex items-center gap-2 mb-2">
                                                                                        <span className="text-xs text-muted-foreground">{sign.valueMin}</span>
                                                                                        <input type="range" className="flex-1 accent-[#082359]" defaultValue={sign.valueDefault} min={sign.valueMin} max={sign.valueMax} step="1" id="valueRange"
                                                                                            onInput={(e) => {
                                                                                                const amountMinus10 = parseFloat(item.amount * 0.9);
                                                                                                const amount10 = parseFloat(item.amount * 0.1);
                                                                                                const amount = parseFloat(item.amount).toFixed(2);
                                                                                                const amountFirst = Math.ceil(parseFloat(amount10));
                                                                                                const instalmentCount = parseInt(e.target.value);
                                                                                                const amountPerMonth = Math.ceil(parseFloat(amountMinus10 / (instalmentCount - 1)));
                                                                                                const totalAmountCalculated = amountFirst + amountPerMonth * (instalmentCount - 1);
                                                                                                const adjustment = amount - totalAmountCalculated;
                                                                                                const adjustedAmountFirst = parseFloat((amountFirst + adjustment).toFixed(2));
                                                                                                document.getElementById("value" + index + index + index + index).textContent = e.target.value;
                                                                                                document.getElementById("partValue" + index + index + index + index).value = amountPerMonth.toLocaleString("cs-CZ") + " Kč";
                                                                                                if (sign.contractStart === "first_15_d_10") {
                                                                                                    document.getElementById("first" + index + index + index + index).value = adjustedAmountFirst.toLocaleString("cs-CZ") + " Kč";
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                        <span className="text-xs text-muted-foreground">{sign.valueMax}</span>
                                                                                    </div>
                                                                                    <p className="text-sm mb-1">Počet splátek: <strong id={"value" + index + index + index + index}>{sign.valueDefault}</strong></p>
                                                                                    {sign.contractStart === "first_15_d_10" ? (
                                                                                        <>
                                                                                            <p className="text-sm mb-1">Datum první splátky: <strong>7 dní od podpisu</strong></p>
                                                                                            <p className="text-sm mb-1">Datum dalších splátek: <strong>15. den v měsíci</strong></p>
                                                                                            <p className="text-sm mb-1">Částka první splátky (~10%): <input className="font-bold bg-transparent border-none outline-none w-24 text-sm" disabled defaultValue={(Math.ceil(parseFloat(item.amount) * 0.1) + (parseFloat(item.amount) - Math.ceil(parseFloat(item.amount) * 0.1) - Math.ceil((parseFloat(item.amount) * 0.9) / (parseInt(sign.valueDefault) - 1)) * (parseInt(sign.valueDefault) - 1))).toLocaleString("cs-CZ") + " Kč"} id={"first" + index + index + index + index} /></p>
                                                                                            <p className="text-sm mb-3">Částka dalších splátek: <input disabled defaultValue={Math.ceil((parseFloat(item.amount) * 0.9) / (parseInt(sign.valueDefault) - 1)).toLocaleString("cs-CZ") + " Kč"} className="font-bold bg-transparent border-none outline-none w-24 text-sm" id={"partValue" + index + index + index + index} /></p>
                                                                                        </>
                                                                                    ) : (
                                                                                        <p className="text-sm mb-1">Částka za splátku: <strong id={"partValue" + index + index + index + index}>{Math.ceil(item.amount / sign.valueDefault).toLocaleString("cs-CZ")}</strong> <strong>{item.currency === "CZK" ? "Kč" : item.currency}</strong></p>
                                                                                    )}
                                                                                    <p className="text-sm font-semibold border-t border-border pt-2 mt-2 mb-3">Celková částka: <strong>{Math.ceil(item.amount.toLocaleString("cs-CZ"))} {item.currency === "CZK" ? "Kč" : item.currency}</strong></p>
                                                                                    <button type="submit" className="button-bankid mt-1"><BankIdLogo />Zobrazit a podepsat</button>
                                                                                </form>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {!sign.settingsEnabled && <ContactCard />}
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {true == false && <SigningHelpBanner />}
                            </section>
                        )}

                        {/* Signed documents — only show when there are signed documents */}
                        {signedDocuments.length > 0 && (
                            <section aria-labelledby="signed-heading">
                                <h2 className="text-base font-semibold text-foreground mb-3" id="signed-heading">Podepsané dokumenty</h2>
                                <div className="space-y-3">
                                    {signedDocuments.map((item, index) => (
                                        <div key={index} className="bg-white rounded-xl border border-border shadow-sm p-4">
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <h4 className="text-sm font-semibold mb-1">{item.title}</h4>
                                                    <span className="text-xs text-muted-foreground">{getDate(item.date)}</span>
                                                    {(item.refNo || item.supplier) && (
                                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                                            {item.refNo && (
                                                                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-0.5">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 shrink-0" aria-hidden="true">
                                                                        <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                                                                    </svg>
                                                                    {item.refNo}
                                                                </span>
                                                            )}
                                                            {item.supplier && (
                                                                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-0.5">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 shrink-0" aria-hidden="true">
                                                                        <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                                                                    </svg>
                                                                    {item.supplier}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap gap-2 shrink-0">
                                                    <Button asChild size="sm" variant="outline">
                                                        <a href={item.uri} target="_blank" rel="noreferrer">
                                                            Zobrazit <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-1" />
                                                        </a>
                                                    </Button>
                                                    <Button asChild size="sm">
                                                        <a href={item.uri} target="_blank" rel="noreferrer" download>
                                                            Stáhnout <FontAwesomeIcon icon={faDownload} className="ml-1" />
                                                        </a>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>
                )}
            </main>
        </>
    );
}
